import BrowseLoadMore from '../../../features/browse/BrowseLoadMore';
import RecoverableImage from '../../../features/images/RecoverableImage';
import useRecordSearch from '../../../features/profile/useRecordSearch';
import { filterRecordSearch } from '../../../features/profile/recordsSearchModel';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiSearch, FiX } from 'react-icons/fi';
import { useWebLocale } from '../../../contexts/WebLocaleContext';
import { useColorScheme } from '../../../contexts/ColorSchemeContext';
import baseline from '../../../app-ui/baseline';
import MeasuredContentTitle from '../../../components/content/MeasuredContentTitle';
import HydratedPlaylistArtwork from '../../../components/content/HydratedPlaylistArtwork';
import useMyRecords from './useMyRecords';
import {
  compactRecordText,
  recordTabs,
  recordCounts,
  formatRecordCount,
  activeRecords,
  hasReviewBody,
  recordPath,
} from './myRecordsModel';
import './MyMusicRecords.css';

const copy = {
  ko: {
    labels: ['평가', '리뷰', '플리', '저장', '댓글'],
    kinds: ['앨범', '곡'],
    name: '음악 기록',
    search: '음악 기록 검색어',
    placeholder: '앨범, 곡, 아티스트, 플리 등을 검색',
    clear: '검색어 지우기',
    loading: '음악 기록을 불러오는 중',
    error: '음악 기록을 불러오지 못했습니다.',
    retry: '다시 시도',
    empty: [
      '아직 평가한 앨범이 없어요',
      '아직 작성한 리뷰가 없어요',
      '아직 만든 플리가 없어요',
      '아직 저장한 항목이 없어요',
      '아직 작성한 댓글이 없어요',
    ],
    tracks: '아직 평가한 곡이 없어요',
    noResult: '검색 결과가 없어요',
    artist: '아티스트',
    more: '더 보기',
  },
  en: {
    labels: ['Ratings', 'Reviews', 'Playlists', 'Saved', 'Comments'],
    kinds: ['Albums', 'Tracks'],
    name: 'Music Log',
    search: 'Search your music log',
    placeholder: 'Search albums, tracks, artists, playlists, and more',
    clear: 'Clear search',
    loading: 'Loading your music log',
    error: 'We could not load the music log.',
    retry: 'Try again',
    empty: [
      'No rated albums yet',
      'No written reviews yet',
      'No playlists created yet',
      'No saved items yet',
      'No comments yet',
    ],
    tracks: 'No rated tracks yet',
    noResult: 'No results found',
    artist: 'Artist',
    more: 'Load more',
  },
};
function Artwork({ item, tab, track }) {
  const playlist = tab === 'lists' ? item : item.list;
  if (playlist) return <HydratedPlaylistArtwork playlist={playlist} />;
  const image = track
    ? item.imageUrl
    : (item.album?.imageUrl ?? item.notice?.imageUrl ?? item.feed?.imageUrl);
  return (
    <div className="pp-record-art" aria-hidden="true">
      {image ? <RecoverableImage src={image} alt="" loading="lazy" fallback={<>♪</>} /> : '♪'}
    </div>
  );
}
function Score({ rating }) {
  const { locale } = useWebLocale();
  const score = Number.isFinite(Number(rating)) ? Number(rating).toFixed(1) : '0.0';
  return (
    <span
      className="pp-record-score"
      aria-label={`${locale === 'en' ? 'Rating' : '평점'} ${score}`}
    >
      <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="#F16438"
          transform="translate(0 .955)"
          d="M12 2L14.47 8.6L21.51 8.91L15.99 13.3L17.88 20.09L12 16.2L6.12 20.09L8.01 13.3L2.49 8.91L9.53 8.6L12 2Z"
        />
      </svg>
      {score}
    </span>
  );
}
function RecordRow({ item, tab, kind, en, words, ownerId }) {
  const track = tab === 'ratings' && kind === 'tracks';
  const title = track
    ? item.title
    : tab === 'lists'
      ? item.title
      : (item.album?.title ??
        item.list?.title ??
        item.notice?.title ??
        item.feed?.title ??
        (en ? 'Item' : '항목'));
  const artist = (track ? item.artistName : item.artist?.name) || words.artist;
  const playlist = tab === 'lists' ? item : null;
  const body =
    tab === 'reviews' || tab === 'saved'
      ? item.review?.content
      : tab === 'comments'
        ? item.comment?.content
        : playlist
          ? en
            ? `${item.viewCount ?? 0} views · ${item.commentCount ?? 0} comments · ${item.bookmarkCount ?? 0} saves`
            : `조회 ${item.viewCount ?? 0} · 댓글 ${item.commentCount ?? 0} · 저장 ${item.bookmarkCount ?? 0}`
          : null;
  const savedLabels = en
    ? { Albums: 'Album', Reviews: 'Review', Lists: 'Playlist', Notice: 'Notice', Feed: 'Feed' }
    : { Albums: '앨범', Reviews: '리뷰', Lists: '플리', Notice: '공지', Feed: '피드' };
  const destination = recordPath(item, tab, kind);
  const Component = destination ? Link : 'div';
  return (
    <Component
      className="pp-record-row"
      {...(destination
        ? {
            to: destination,
            state:
              track && ownerId != null
                ? { recordTrackRating: { trackId: item.trackId, viewerId: ownerId } }
                : undefined,
          }
        : {})}
    >
      <Artwork item={item} tab={tab} track={track} />
      <span className={`pp-record-copy${track ? ' is-track' : ''}`}>
        <MeasuredContentTitle
          title={title}
          explicit={!track && item.album?.contentRating === 'EXPLICIT'}
        />
        {tab === 'ratings' || tab === 'reviews' ? (
          <span className="pp-record-meta">
            <span className="pp-record-artist">{artist}</span>
            {tab === 'reviews' && (
              <>
                <span>·</span>
                <Score rating={item.review?.rating} />
              </>
            )}
          </span>
        ) : (
          <span className="pp-record-secondary">
            {playlist
              ? `${en ? (item.type === 'ALBUM' ? 'Album playlist' : 'Track playlist') : item.type === 'ALBUM' ? '앨범 플리' : '곡 플리'} · ${item.type === 'ALBUM' ? item.albumCount : item.trackCount}${en ? ' items' : '개'}`
              : tab === 'comments'
                ? item.comment?.updatedAt
                : (savedLabels[item.category] ?? item.category)}
          </span>
        )}
        {track && <span className="pp-record-secondary">{item.albumTitle}</span>}
        {body?.trim() && <span className="pp-record-body">{compactRecordText(body)}</span>}
      </span>
      <span className="pp-record-trailing">
        {tab === 'ratings' && <Score rating={track ? item.rating : item.review?.rating} />}{' '}
        {destination && <FiChevronRight size={18} aria-hidden="true" />}
      </span>
    </Component>
  );
}
export default function MyMusicRecords({ profile, ...props }) {
  const records = useMyRecords(profile);
  return <MusicRecords {...props} profile={profile} records={records} />;
}
export function MusicRecords({
  profile,
  selectedTab,
  overview = false,
  records,
  basePath = '/mypage',
}) {
  const navigate = useNavigate();
  const { locale } = useWebLocale(),
    { resolvedMode } = useColorScheme();
  const en = locale === 'en',
    words = copy[en ? 'en' : 'ko'];
  const colors = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const { enabled, data, queries, privateTabs = {}, ownerId } = records;
  const [composing, setComposing] = useState(false);
  const fontProbe = useRef(null);
  const [expanded, setExpanded] = useState(false);
  useLayoutEffect(() => {
    const measure = () => {
      if (fontProbe.current) setExpanded(fontProbe.current.getBoundingClientRect().height >= 22.4);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (fontProbe.current) observer?.observe(fontProbe.current);
    return () => observer?.disconnect();
  }, [enabled]);
  const [params, setParams] = useSearchParams();
  const tab = recordTabs.includes(selectedTab) ? selectedTab : 'reviews';
  const kind = params.get('kind') === 'tracks' ? 'tracks' : 'albums',
    query = params.get('q') || '';
  const visible = Math.max(10, Math.min(100000, Number(params.get('limit')) || 10));
  const loadMore = useCallback(async () => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('limit', String(visible + 10));
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  }, [setParams, visible]);
  const setControl = (key, value) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    next.delete('limit');
    setParams(next, { replace: true });
  };
  const collections = {
    ratings: data.albums ?? [],
    reviews: (data.albums ?? []).filter(hasReviewBody),
    lists: data.lists ?? [],
    saved: activeRecords(data.saved) ?? [],
    comments: activeRecords(data.comments) ?? [],
  };
  const entities = useRecordSearch(query, profile.id, enabled && !overview, composing);
  const filtered = Object.fromEntries(
    recordTabs.map((key) => [key, filterRecordSearch(collections[key], query, key, entities)]),
  );
  const tracks = filterRecordSearch(data.tracks, query, 'tracks');
  const counts = recordCounts(data, profile);
  if (query.trim())
    for (const key of recordTabs) {
      const resource = key === 'ratings' || key === 'reviews' ? 'albums' : key;
      counts[key] = data[resource] == null ? undefined : filtered[key].length;
      if (key === 'ratings')
        counts[key] =
          data.albums == null || data.tracks == null
            ? undefined
            : filtered.ratings.length + tracks.length;
    }
  const resource =
    tab === 'ratings'
      ? kind === 'tracks'
        ? 'tracks'
        : 'albums'
      : tab === 'reviews'
        ? 'albums'
        : tab;
  const request = queries[resource];
  const items = tab === 'ratings' && kind === 'tracks' ? tracks : filtered[tab];
  const isPrivate = !!privateTabs[tab];
  const failed = overview
    ? []
    : [[resource, request]].filter(([key, q]) => !privateTabs[key] && q.isError);
  if (!enabled) return null;
  return (
    <section
      className={`pp-my-records${expanded ? ' is-expanded' : ''}`}
      aria-label={words.name}
      data-profile-records-ready={
        overview || isPrivate || (!request.isPending && !entities.pending)
      }
      style={{
        '--record-text': colors.text,
        '--record-secondary': colors.textSecondary,
        '--record-muted': colors.textMuted,
        '--record-border': colors.divider,
        '--record-bg': colors.background,
        '--record-surface': colors.surfaceMuted,
        '--record-body': colors.reviewBody,
      }}
    >
      <span ref={fontProbe} className="pp-record-font-probe" aria-hidden="true" />
      {!overview && (
        <>
          <header className="pp-record-header">
            <button
              type="button"
              aria-label={en ? 'Back' : '뒤로'}
              onClick={() =>
                window.history.state?.idx > 0 ? navigate(-1) : navigate(basePath, { replace: true })
              }
            >
              <FiChevronLeft size={23} />
            </button>
            <h1>{en ? `${profile.nickname}’s Music Log` : `${profile.nickname}님의 음악 기록`}</h1>
            <span />
          </header>
          <div className="pp-record-search">
            <FiSearch size={20} aria-hidden="true" />
            <input
              type="search"
              aria-label={words.search}
              placeholder={words.placeholder}
              value={query}
              onCompositionStart={() => setComposing(true)}
              onCompositionEnd={() => setComposing(false)}
              onChange={(event) => setControl('q', event.target.value)}
            />
            {query && (
              <button type="button" aria-label={words.clear} onClick={() => setControl('q', '')}>
                <FiX size={18} />
              </button>
            )}
          </div>
        </>
      )}
      <nav className="pp-record-stats" aria-label={words.name}>
        {recordTabs.map((key, index) => (
          <Link
            key={key}
            replace={!overview}
            to={`${basePath}/${key}${query ? `?${new URLSearchParams({ q: query, kind })}` : kind === 'tracks' ? '?kind=tracks' : ''}`}
            aria-current={!overview && tab === key ? 'page' : undefined}
          >
            <strong>{formatRecordCount(privateTabs[key] ? undefined : counts[key])}</strong>
            <span>{words.labels[index]}</span>
          </Link>
        ))}
      </nav>
      {!overview && (
        <>
          {tab === 'ratings' && (
            <div className="pp-record-segments">
              {['albums', 'tracks'].map((value, index) => (
                <button
                  type="button"
                  key={value}
                  aria-pressed={kind === value}
                  onClick={() => setControl('kind', value)}
                >
                  {words.kinds[index]}{' '}
                  {formatRecordCount(
                    data[value] == null
                      ? undefined
                      : value === 'tracks'
                        ? tracks.length
                        : filtered.ratings.length,
                  )}
                </button>
              ))}
            </div>
          )}
          {failed.length > 0 && (
            <div className="pp-record-error" role="alert">
              <span>{words.error}</span>
              <button type="button" onClick={() => failed.forEach(([, q]) => q.refetch())}>
                {words.retry}
              </button>
            </div>
          )}
          {isPrivate ? (
            <div className="pp-record-private" role="status">
              <strong>
                {en
                  ? tab === 'saved'
                    ? 'Saved items are private'
                    : 'Comment history is private'
                  : tab === 'saved'
                    ? '저장 목록이 비공개예요'
                    : '댓글 기록이 비공개예요'}
              </strong>
              <span>
                {en
                  ? tab === 'saved'
                    ? 'This member does not show saved items on their profile.'
                    : 'This member does not show their comments on their profile.'
                  : tab === 'saved'
                    ? '이 회원은 프로필에서 저장 목록을 공개하지 않습니다.'
                    : '이 회원은 프로필에서 작성 댓글을 공개하지 않습니다.'}
              </span>
            </div>
          ) : request.isPending ? (
            <p className="pp-record-empty" role="status">
              {words.loading}
            </p>
          ) : !request.isError && items.length === 0 ? (
            <div className="pp-record-private">
              <strong>
                {query.trim()
                  ? words.noResult
                  : tab === 'ratings' && kind === 'tracks'
                    ? words.tracks
                    : words.empty[recordTabs.indexOf(tab)]}
              </strong>
              {query.trim() ? (
                <span>
                  {en
                    ? 'Try another album, track, artist, review phrase, or playlist title.'
                    : '앨범, 곡, 아티스트, 리뷰 내용이나 플리 제목을 다시 검색해보세요.'}
                </span>
              ) : (
                !(tab === 'ratings' && kind === 'tracks') && (
                  <span>
                    {en
                      ? `${profile.nickname}’s music log will appear here.`
                      : `${profile.nickname}님의 음악 기록이 이곳에 표시됩니다.`}
                  </span>
                )
              )}
            </div>
          ) : null}
          {!isPrivate && !request.isPending && !request.isError && (
            <div className="pp-record-list">
              {items.slice(0, visible).map((item, index) => (
                <RecordRow
                  key={`${tab}-${kind}-${item.review?.id ?? item.trackId ?? item.comment?.id ?? item.id ?? index}`}
                  item={item}
                  tab={tab}
                  kind={kind}
                  en={en}
                  words={words}
                  ownerId={ownerId}
                />
              ))}
            </div>
          )}
          {!isPrivate && !request.isPending && !request.isError && items.length > visible && (
            <BrowseLoadMore
              key={`${profile.id}:${tab}:${kind}:${query}`}
              restored={params.has('limit')}
              autoLoadBatches={Infinity}
              preloadDistance={400}
              query={{
                data: items,
                hasNextPage: items.length > visible,
                batchCount: Math.ceil(visible / 10),
                fetchNextPage: loadMore,
              }}
            />
          )}
        </>
      )}
    </section>
  );
}
